/**
 * Where lobbyist money goes after it stops being about a member.
 *
 * A reader who follows a lobbying firm's giving hits a wall: a large part of
 * it is filed against "DSCC" or "Natl Rep Sen Cmte(NRSC)-Federal Acct", and
 * a party committee is not a member of Congress, so the trail ended there.
 * This is the next hop — what the four national campaign committees were
 * given, and what they then spent on named candidates.
 *
 * The page is built around one refusal. Money out comes in four categories
 * and $411.4m of the $601.8m is 24A: advertising bought to attack a
 * candidate, which the FEC files under the name of the candidate it attacks.
 * Add that to the 24C coordinated column and you have reported support that
 * did not happen. So there is no total anywhere on this page — not per
 * committee, not across them — and every figure carries the direction the
 * money was pointing.
 */

import { Link } from "react-router-dom";
import { api, type PartyCommittee, type PartyCommitteeIndex } from "../api";
import { useAsync } from "../useAsync";
import { Problem, Spinner } from "../components/bits";
import { CommitteeTable } from "../components/CommitteeTable";
import {
  EFFECTS,
  EFFECT_ORDER,
  NAMED,
  biggest,
  ceiling,
  effectRows,
  figure,
  national,
  nonFederal,
  share,
} from "../components/committees";

export function PartyCommittees() {
  const found = useAsync(() => api.partyCommittees(), []);
  if (found.error) return <Problem error={found.error} retry={found.reload} />;
  if (found.loading || !found.data) return <Spinner label="Counting the committees…" />;
  return <Committees data={found.data} />;
}

/** Split from the fetching so the page can be rendered in a test against a
 *  payload, which is the only way to assert that no total appears on it. */
export function Committees({ data }: { data: PartyCommitteeIndex }) {
  const four = national(data.committees);
  const scale = ceiling(four);
  const heldBack = four.reduce((sum, c) => sum + nonFederal(c), 0);
  // Derived rather than asserted. It happens to be true of all four, and if
  // a later cycle changes that the sentence should change with it.
  const allAttack = four.length === 4 && four.every((c) => biggest(c.money_out)?.key === "opposing");

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <p className="text-sm">
        <Link to="/money" className="text-muted hover:text-ink">
          ← Where the money goes
        </Link>
      </p>

      <h1 className="mt-3 font-display text-3xl font-semibold">The party committees</h1>
      <p className="mt-2 max-w-3xl text-muted">
        Registered lobbyists disclose giving four committees — the DSCC, NRSC, DCCC and NRCC —
        around $141m over six years. None of them is a member of Congress, so on the rest of this
        site that money went into “elsewhere” and stopped. This is where it goes next: what each
        committee was given, and what each then spent on candidates by name.
      </p>

      <Categories />

      {four.length === 0 ? (
        <p className="mt-12 rounded-2xl border border-line bg-card p-8 text-muted">
          No party committee spending is held yet.
        </p>
      ) : (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-semibold">
            What they were given, and what they spent
          </h2>
          <p className="mt-1.5 max-w-3xl text-muted">
            Every bar below is drawn against the same scale, so the four committees can be read
            against each other as well as against themselves.{" "}
            {allAttack && (
              <span className="text-ink">
                For all four, the largest category by far is money spent attacking a candidate, and
                money handed to a campaign is a rounding error beside it.
              </span>
            )}
          </p>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {four.map((committee) => (
              <CommitteeCard key={committee.committee_id} committee={committee} scale={scale} />
            ))}
          </div>
        </section>
      )}

      {heldBack > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-semibold">
            The {figure(heldBack)} that can never reach a candidate
          </h2>
          <p className="mt-1.5 max-w-3xl text-muted">
            Since 2014 a national party committee may hold separate accounts for its headquarters
            building, for recounts and legal proceedings, and for its nominating convention, each
            with a contribution limit several times the ordinary one. Lobbyists name them on the
            filing, and {figure(heldBack)} of what these four were given arrived in one. It is
            money to the committee and it is not money that can become a contribution to a
            candidate, so the federal figure is the one shown beside money out above.
          </p>
        </section>
      )}

      <Coverage coverage={data.coverage} />

      {data.committees.length > 0 && <CommitteeTable held={data.committees} />}
    </div>
  );
}

/**
 * The vocabulary, before any figure that uses it.
 *
 * Placed above the committees deliberately. A reader who meets "$157.2m
 * opposing" before meeting what opposing means will read it as the DCCC's
 * war chest, which is the single most likely misreading of this page.
 */
function Categories() {
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl font-semibold">Four kinds of spending</h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        The FEC records what a committee did with each payment as a two-character code. They are
        four different acts, not four sizes of the same one, and this site never adds them
        together — see below for what that would report.
      </p>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {EFFECT_ORDER.map((key) => {
          const facts = EFFECTS[key];
          const against = facts.direction === "against";
          return (
            <div
              key={key}
              className={`rounded-2xl border p-5 shadow-sm ${
                against ? "border-gop/40 bg-gop/5" : "border-line bg-card"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {facts.codes.map((code) => (
                  <span
                    key={code}
                    className="tnum rounded bg-sunk px-1.5 py-0.5 text-xs font-semibold text-muted"
                  >
                    {code}
                  </span>
                ))}
              </div>
              <h3 className="mt-2.5 font-medium">{facts.label}</h3>
              <p
                className={`mt-1 text-xs font-semibold uppercase tracking-wide ${
                  against ? "text-gop" : "text-muted"
                }`}
              >
                money {facts.tag}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted">{facts.blurb}</p>
            </div>
          );
        })}
      </div>

      {/* The worked example, because the rule on its own sounds pedantic and
          the consequence does not. Written out rather than derived: it is a
          closed cycle, so the two figures are final, and picking the pair
          programmatically would sometimes choose two candidates in different
          states, where adding them is wrong for a duller reason. */}
      <div className="mt-4 rounded-2xl border border-line bg-sunk p-5">
        <p className="max-w-3xl text-sm leading-relaxed">
          <span className="font-medium">Why they are never added.</span> In the 2024 Michigan Senate
          race the DSCC spent $12.3m attacking Mike Rogers and $2.0m coordinated with Elissa
          Slotkin’s campaign. Summed, that is “$14.3m of DSCC support for Slotkin”, which is wrong
          about the amount and about who most of the money was aimed at. Both figures are real and
          they belong in different columns, so that is where this site keeps them.
        </p>
      </div>
    </section>
  );
}

function CommitteeCard({ committee, scale }: { committee: PartyCommittee; scale: number }) {
  const named = NAMED[committee.name.trim().toUpperCase()];
  const held = nonFederal(committee);

  return (
    <div className="rounded-2xl border border-line bg-card p-6 shadow-sm">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <Link
          to={`/money/committees/${committee.committee_id}`}
          className="font-display text-2xl font-semibold hover:text-brand"
        >
          {committee.name}
        </Link>
        {named && <span className="text-sm font-medium text-muted">{named.whose}</span>}
      </div>
      {named && <p className="mt-0.5 text-sm text-faint">{named.full}</p>}

      <div className="mt-4 border-t border-line pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-faint">
          In, from registered lobbyists
        </p>
        <p className="tnum mt-1 font-display text-2xl font-semibold">
          {figure(committee.money_in_federal)}
        </p>
        <p className="mt-1 text-sm text-muted">
          to the federal account, from {committee.gifts.toLocaleString()} disclosed cheques
          {held > 0 && (
            <>
              . A further {figure(held)} went to headquarters, recount and convention accounts,
              which cannot be paid to a candidate
            </>
          )}
          .
        </p>
      </div>

      <div className="mt-4 border-t border-line pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-faint">
          Out, on named candidates · {committee.transactions.toLocaleString()} transactions
        </p>
        <ul className="mt-3 space-y-2.5">
          {effectRows(committee.money_out).map((row) => {
            const against = row.facts.direction === "against";
            // A floor on the width, so half a per cent is a visible mark. A
            // bar of no width reads as "nothing filed", which is a different
            // claim from "almost nothing".
            const width = Math.max(row.amount > 0 ? 0.4 : 0, (row.amount / scale) * 100);
            return (
              <li key={row.key}>
                <div className="flex items-baseline gap-2 text-sm">
                  <span className={`flex-1 ${against ? "font-medium text-gop" : "text-muted"}`}>
                    {row.facts.label}
                  </span>
                  <span className={`text-xs ${against ? "text-gop" : "text-faint"}`}>
                    {row.facts.tag}
                  </span>
                  <span className="tnum w-16 text-right font-medium">{figure(row.amount)}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-sunk">
                  <div
                    data-bar={`${committee.name}-${row.key}`}
                    style={{ width: `${width.toFixed(1)}%` }}
                    className={`h-full rounded-full ${against ? "bg-gop" : "bg-brand"}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <Link
        to={`/money/committees/${committee.committee_id}`}
        className="mt-4 inline-block text-sm font-medium text-brand hover:underline"
      >
        Who gave it, and who it was spent on →
      </Link>
    </div>
  );
}

/**
 * How much of this resolves to somebody who sits in Congress.
 *
 * Published per category as well as overall, because a single figure would
 * read as a matching failure. Most of what a party committee spends is aimed
 * at challengers, and the whole of the attacking category is aimed at the
 * other party's candidates, who by definition did not win the seat.
 */
function Coverage({ coverage }: { coverage: PartyCommitteeIndex["coverage"] }) {
  if (!coverage.total) return null;
  return (
    <section className="mt-14">
      <h2 className="font-display text-2xl font-semibold">
        How much of this reaches a sitting member
      </h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        {share(coverage.matched, coverage.total)} of these{" "}
        {coverage.total.toLocaleString()} transactions name a candidate who is a member of Congress
        in this corpus, and {share(coverage.dollars_matched, coverage.dollars)} of the dollars. The
        rest are mostly challengers who lost. That is the record being right rather than broken —
        but it does mean every member page below is a subset, and the subset is not the whole.
      </p>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {coverage.by_effect.map((row) => {
          const facts = row.effect ? EFFECTS[row.effect] : null;
          return (
            <li
              key={row.effect ?? "unclassified"}
              className="rounded-2xl border border-line bg-card p-5 shadow-sm"
            >
              <p className="tnum font-display text-2xl font-semibold">
                {share(row.matched, row.total)}
              </p>
              <p className="mt-0.5 text-sm font-medium">
                of {facts?.label.toLowerCase() ?? "unclassified spending"}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-faint">
                {figure(row.dollars)} across {row.total.toLocaleString()} transactions, of which{" "}
                {row.matched.toLocaleString()} name a sitting member.
              </p>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 max-w-3xl text-sm text-faint">
        The categories do not resolve alike, which is why they are not reported as one figure. A
        cheque nearly always goes to somebody the party expects to seat; an advertisement attacking
        a candidate is aimed at the other party, so it lands on a sitting member only when the
        person attacked won anyway.
      </p>
    </section>
  );
}
